import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { LogOut, Users, Mail, Palette, MapPin, Download, Plus, Trash2, Send, Eye, Sparkles } from "lucide-react";
import { toast } from "sonner";
import AiAssistantPanel from "@/components/AiAssistantPanel";

const PROSPECT_STATUSES = [
  { k: "a_contacter",   l: "À contacter",  cls: "bg-fg/10 text-fg" },
  { k: "contacte",      l: "Contacté",     cls: "bg-blue-500/15 text-blue-700" },
  { k: "en_discussion", l: "En discussion",cls: "bg-amber-500/15 text-amber-700" },
  { k: "rdv",           l: "Rendez-vous",  cls: "bg-purple-500/15 text-purple-700" },
  { k: "engage",        l: "Engagé",       cls: "bg-emerald-500/15 text-emerald-700" },
  { k: "refuse",        l: "Refusé",       cls: "bg-red-500/15 text-red-700" },
];

export default function AdminPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [mec, setMec] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [apps, setApps] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [dlFilter, setDlFilter] = useState({ doc: "all", verified: "all", search: "" });
  const [lettres, setLettres] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [lettreDraft, setLettreDraft] = useState({
    id: null, title: "", month_label: "", hero_image: "", artist_name: "",
    escale_name: "", atelier_photo: "", body_html: "", signature: "La Fée du canal",
    segments: [],
  });
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogDraft, setBlogDraft] = useState({
    id: null, slug: "", title: "", subtitle: "", hero_image: "", hero_caption: "",
    author: "Les Arteurs", excerpt: "", body_html: "", tags: [], status: "draft",
  });
  const [rencontres, setRencontres] = useState([]);
  const [rDraft, setRDraft] = useState({
    id: null, image_url: "", photographer_name: "", photographer_email: "",
    location: "", taken_at: "", message: "", featured: false, status: "approved",
  });
  const [comments, setComments] = useState([]);
  const [comiteReqs, setComiteReqs] = useState([]);
  const [payments, setPayments] = useState([]);

  const loadPayments = () => api.get("/admin/payments").then(r => setPayments(r.data)).catch(() => {});

  const loadComiteReqs = () => api.get("/admin/comite-requests").then(r => setComiteReqs(r.data)).catch(() => {});

  const loadProspects = () => api.get("/admin/prospects").then(r => setProspects(r.data)).catch(() => {});
  const loadDownloads = () => api.get("/admin/download-requests").then(r => setDownloads(r.data)).catch(() => {});
  const loadLettres = () => api.get("/admin/lettres").then(r => setLettres(r.data)).catch(() => {});
  const loadAudiences = () => api.get("/admin/lettres/audiences").then(r => setAudiences(r.data)).catch(() => {});
  const loadBlog = () => api.get("/admin/blog").then(r => setBlogPosts(r.data)).catch(() => {});
  const loadRencontres = () => api.get("/admin/rencontres").then(r => setRencontres(r.data)).catch(() => {});
  const loadComments = () => api.get("/admin/comments").then(r => setComments(r.data)).catch(() => {});

  useEffect(() => {
    if (!user) return;
    api.get("/admin/stats").then(r => setStats(r.data)).catch(() => {});
    api.get("/admin/subscribers").then(r => setSubs(r.data)).catch(() => {});
    api.get("/admin/messages").then(r => setMsgs(r.data)).catch(() => {});
    api.get("/admin/mecenat/stats").then(r => setMec(r.data)).catch(() => {});
    api.get("/admin/applications").then(r => setApps(r.data)).catch(() => {});
    loadProspects();
    loadDownloads();
    loadLettres();
    loadAudiences();
    loadBlog();
    loadRencontres();
    loadComments();
    loadComiteReqs();
    loadPayments();
  }, [user]);

  if (!user) return null;

  const adoptCount = apps.filter(a => a.kind === "adoption_artiste").length;
  const tabs = [
    { k: "stats", l: "Vue d'ensemble" },
    { k: "subs", l: `Abonnés (${subs.length})` },
    { k: "msgs", l: `Messages (${msgs.length})` },
    { k: "comite", l: `Comité (${comiteReqs.filter(c => c.status !== "traite").length}/${comiteReqs.length})` },
    { k: "payments", l: `Paiements (${payments.filter(p => p.payment_status === "paid").length})` },
    { k: "apps", l: `Candidatures (${apps.length})${adoptCount ? " · " + adoptCount + " adoption" + (adoptCount > 1 ? "s" : "") : ""}` },
    { k: "mecenat", l: `Mécénat PDF (${mec?.total ?? 0})` },
    { k: "downloads", l: `Téléchargements (${downloads.length})` },
    { k: "lettres", l: `Lettres du Fil (${lettres.length})` },
    { k: "carnet", l: `Le Carnet (${blogPosts.length})` },
    { k: "comments", l: `Commentaires (${comments.length})` },
    { k: "rencontres", l: `Rencontres (${rencontres.length})` },
    { k: "prospects", l: `Prospection (${prospects.length})` },
    { k: "ai", l: "Assistant IA" },
  ];

  const patchProspect = async (id, changes) => {
    // Optimistic update
    setProspects((cur) => cur.map((p) => (p.id === id ? { ...p, ...changes } : p)));
    try {
      const { data } = await api.patch(`/admin/prospects/${id}`, changes);
      setProspects((cur) => cur.map((p) => (p.id === id ? data : p)));
    } catch {
      loadProspects();
    }
  };

  const addProspect = async () => {
    const name = window.prompt("Nom de la fondation / entreprise :");
    if (!name) return;
    try {
      await api.post("/admin/prospects", { name, category: "Autre" });
      loadProspects();
    } catch {
      /* ignore */
    }
  };

  const removeProspect = async (id, name) => {
    if (!window.confirm(`Supprimer « ${name} » de la liste ?`)) return;
    try {
      await api.delete(`/admin/prospects/${id}`);
      loadProspects();
    } catch {
      /* ignore */
    }
  };

  return (
    <main className="pt-32 pb-20 px-6 md:px-12 max-w-[1600px] mx-auto" data-testid="admin-page">
      <div className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <div className="meta-label text-cinabre">§ Back-office · Les Arteurs</div>
          <h1 className="font-serif font-light text-5xl md:text-6xl leading-[0.9] tracking-tight mt-4">Le Fil du Midi.</h1>
        </div>
        <button onClick={async () => { await logout(); nav("/"); }} className="btn-editorial ghost" data-testid="admin-logout">
          <LogOut size={14} /> Déconnexion
        </button>
      </div>

      {stats && (
        <div className="mt-14 grid grid-cols-2 md:grid-cols-5 gap-px bg-fg/15 border border-fg/15">
          {[
            { k: "subscribers", l: "Abonnés", i: Mail },
            { k: "messages", l: "Messages", i: Mail },
            { k: "artists", l: "Artistes", i: Palette },
            { k: "sites", l: "Sites", i: MapPin },
            { k: "users", l: "Utilisateurs", i: Users },
          ].map(({ k, l, i: Icon }) => (
            <div key={k} className="bg-bg p-6" data-testid={`stat-${k}`}>
              <div className="flex items-center justify-between">
                <div className="meta-label text-fg/60">{l}</div>
                <Icon size={16} className="text-cinabre" />
              </div>
              <div className="font-serif text-5xl mt-3">{stats[k]}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-14 flex gap-4 border-b border-fg/15">
        {tabs.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-1 pb-3 font-mono text-[11px] tracking-[0.24em] uppercase border-b-2 transition-colors ${tab === t.k ? "border-cinabre text-cinabre" : "border-transparent text-fg/60 hover:text-fg"}`}
            data-testid={`admin-tab-${t.k}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      <div className="mt-10">
        {tab === "stats" && (
          <div>
            <p className="font-serif text-2xl italic max-w-2xl leading-relaxed">
              Bienvenue dans le back-office. Suivez les inscriptions, les messages et les indicateurs du parcours.
            </p>
            <div className="mt-10" data-testid="admin-visits">
              <div className="meta-label text-cinabre">§ Fréquentation du site</div>
              <div className="mt-3 font-serif text-4xl">{stats?.total_views ?? 0} <span className="text-lg text-fg/60">visites de pages</span></div>
              <div className="mt-6 meta-label text-fg/60">— Rubriques les plus visitées</div>
              <table className="mt-3 w-full max-w-xl text-sm">
                <tbody>
                  {(stats?.top_pages || []).map((p) => (
                    <tr key={p.path} className="border-b border-fg/10">
                      <td className="py-2 font-mono text-xs">{p.path}</td>
                      <td className="py-2 text-right font-mono tabular-nums">{p.views}</td>
                    </tr>
                  ))}
                  {(!stats?.top_pages || stats.top_pages.length === 0) && (
                    <tr><td className="py-3 text-fg/50">Les visites s'afficheront ici dès les premières consultations.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {tab === "subs" && (
          <table className="w-full text-sm" data-testid="admin-subs-table">
            <thead>
              <tr className="border-b border-fg/20 text-left">
                <th className="py-3 meta-label text-fg/60">Email</th>
                <th className="py-3 meta-label text-fg/60">Prénom</th>
                <th className="py-3 meta-label text-fg/60">Date</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.id} className="border-b border-fg/10">
                  <td className="py-3 font-serif text-lg">{s.email}</td>
                  <td className="py-3">{s.first_name || "—"}</td>
                  <td className="py-3 font-mono text-xs text-fg/60">{s.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
              {subs.length === 0 && <tr><td colSpan={3} className="py-8 text-fg/50">Aucun abonné pour l'instant.</td></tr>}
            </tbody>
          </table>
        )}
        {tab === "msgs" && (
          <div className="space-y-6" data-testid="admin-msgs-list">
            {msgs.map((m) => (
              <div key={m.id} className="border border-fg/15 p-6">
                <div className="flex justify-between text-fg/60 font-mono text-[10px] tracking-[0.24em] uppercase">
                  <span>{m.name} · {m.email}</span>
                  <span>{m.created_at?.slice(0, 10)}</span>
                </div>
                <div className="font-serif text-2xl mt-3">{m.subject}</div>
                <p className="mt-3 text-fg/80 leading-relaxed">{m.message}</p>
              </div>
            ))}
            {msgs.length === 0 && <div className="py-8 text-fg/50">Aucun message.</div>}
          </div>
        )}
        {tab === "comite" && (
          <div className="space-y-6" data-testid="admin-comite-list">
            <div>
              <div className="meta-label text-cinabre">§ Demandes · Liste du comité de sélection</div>
              <h2 className="font-serif text-3xl md:text-4xl mt-2 tracking-tight">Qui demande la liste ?</h2>
              <p className="mt-2 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50">
                {comiteReqs.filter(c => c.status !== "traite").length} à traiter · {comiteReqs.length} au total
              </p>
            </div>
            {comiteReqs.map((c) => {
              const done = c.status === "traite";
              return (
                <div key={c.id} className={`border p-6 ${done ? "border-fg/10 opacity-60" : "border-cinabre/40 bg-cinabre/[0.03]"}`} data-testid={`comite-req-${c.id}`}>
                  <div className="flex justify-between text-fg/60 font-mono text-[10px] tracking-[0.24em] uppercase flex-wrap gap-2">
                    <span className={done ? "" : "text-cinabre"}>{done ? "Traité" : "Nouveau"}</span>
                    <span>{c.created_at?.slice(0, 10)}</span>
                  </div>
                  <div className="font-serif text-2xl mt-3">{c.name}</div>
                  <div className="mt-1 text-fg/70">
                    <a href={`mailto:${c.email}`} className="underline underline-offset-2 hover:text-cinabre transition-colors">{c.email}</a>
                    {c.organization && <span> · {c.organization}</span>}
                  </div>
                  {c.reason && <p className="mt-3 text-fg/80 leading-relaxed">{c.reason}</p>}
                  <div className="mt-4 flex gap-3 flex-wrap">
                    <button
                      onClick={async () => {
                        try {
                          const { data } = await api.patch(`/admin/comite-requests/${c.id}`);
                          setComiteReqs(cur => cur.map(x => x.id === c.id ? { ...x, status: data.status } : x));
                        } catch { toast.error("Erreur de mise à jour."); }
                      }}
                      className="btn-editorial ghost text-xs"
                      data-testid={`comite-req-toggle-${c.id}`}
                    >
                      {done ? "Remettre en nouveau" : "Marquer traité"}
                    </button>
                    <a href={`mailto:${c.email}?subject=Liste%20du%20comit%C3%A9%20de%20s%C3%A9lection%20%E2%80%94%20Le%20Fil%20du%20Midi`} className="btn-editorial ghost text-xs">
                      <Send size={12} /> Répondre
                    </a>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Supprimer la demande de « ${c.name} » ?`)) return;
                        try {
                          await api.delete(`/admin/comite-requests/${c.id}`);
                          setComiteReqs(cur => cur.filter(x => x.id !== c.id));
                        } catch { toast.error("Erreur de suppression."); }
                      }}
                      className="btn-editorial ghost text-xs text-red-600"
                      data-testid={`comite-req-delete-${c.id}`}
                    >
                      <Trash2 size={12} /> Supprimer
                    </button>
                  </div>
                </div>
              );
            })}
            {comiteReqs.length === 0 && <div className="py-8 text-fg/50">Aucune demande pour l'instant.</div>}
          </div>
        )}
        {tab === "payments" && (
          <div data-testid="admin-payments">
            <div className="mb-8">
              <div className="meta-label text-cinabre">§ Dons & Adhésions en ligne</div>
              <h2 className="font-serif text-3xl md:text-4xl mt-2 tracking-tight">Paiements Stripe</h2>
              <p className="mt-2 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50">
                {(payments.filter(p => p.payment_status === "paid").reduce((s, p) => s + (p.amount || 0), 0) / 100).toFixed(0)} € encaissés · {payments.filter(p => p.payment_status === "paid").length} paiement{payments.filter(p => p.payment_status === "paid").length > 1 ? "s" : ""} confirmé{payments.filter(p => p.payment_status === "paid").length > 1 ? "s" : ""}
              </p>
            </div>
            <table className="w-full text-sm" data-testid="admin-payments-table">
              <thead>
                <tr className="border-b border-fg/20 text-left">
                  <th className="py-3 meta-label text-fg/60">Date</th>
                  <th className="py-3 meta-label text-fg/60">Type</th>
                  <th className="py-3 meta-label text-fg/60">Nom</th>
                  <th className="py-3 meta-label text-fg/60">Email</th>
                  <th className="py-3 meta-label text-fg/60">Montant</th>
                  <th className="py-3 meta-label text-fg/60">Statut</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.session_id} className="border-b border-fg/10">
                    <td className="py-3 font-mono text-xs text-fg/60">{p.created_at?.slice(0, 10)}</td>
                    <td className="py-3">
                      {p.kind === "adhesion" ? "Adhésion" : p.kind === "boutique" ? (
                        <div>Boutique<div className="text-xs text-fg/60 max-w-[180px] truncate">{p.product_name}{p.edition_number ? ` · n° ${p.edition_number}/30` : ""}{p.size ? ` · ${p.size}` : ""}</div></div>
                      ) : "Don"}
                    </td>
                    <td className="py-3 font-serif text-lg">{p.name}</td>
                    <td className="py-3">{p.email}</td>
                    <td className="py-3 font-mono tabular-nums">{((p.amount || 0) / 100).toFixed(0)} €</td>
                    <td className="py-3">
                      <span className={`px-2 py-1 font-mono text-[10px] tracking-[0.15em] uppercase ${
                        p.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-700"
                        : p.payment_status === "pending" ? "bg-amber-500/15 text-amber-700"
                        : "bg-fg/10 text-fg/60"
                      }`}>
                        {p.payment_status === "paid" ? "Payé" : p.payment_status === "pending" ? "En attente" : p.payment_status}
                      </span>
                    </td>
                  </tr>
                ))}
                {payments.length === 0 && <tr><td colSpan={6} className="py-8 text-fg/50">Aucun paiement pour l'instant.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        {tab === "apps" && (
          <div className="space-y-6" data-testid="admin-apps">
            <div className="flex items-baseline justify-between flex-wrap gap-3">
              <div>
                <div className="meta-label text-cinabre">§ Candidatures & Adoptions</div>
                <h2 className="font-serif text-3xl md:text-4xl mt-2 tracking-tight">Toutes les demandes reçues</h2>
                <p className="mt-2 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50">
                  {adoptCount} adoption{adoptCount > 1 ? "s" : ""} d'artiste · {apps.length - adoptCount} autre{apps.length - adoptCount > 1 ? "s" : ""} candidature{apps.length - adoptCount > 1 ? "s" : ""}
                </p>
              </div>
            </div>
            {apps.map((a) => {
              const isAdopt = a.kind === "adoption_artiste";
              return (
                <div
                  key={a.id}
                  className={`border p-6 ${isAdopt ? "border-cinabre bg-cinabre/5" : "border-fg/15"}`}
                  data-testid={`app-${a.id}`}
                >
                  <div className="flex justify-between text-fg/60 font-mono text-[10px] tracking-[0.24em] uppercase flex-wrap gap-2">
                    <span className={isAdopt ? "text-cinabre" : "text-cinabre"}>
                      {isAdopt ? "★ J'adopte un artiste" : (a.kind_label || a.kind)}
                    </span>
                    <span>{a.created_at?.slice(0, 16).replace('T', ' ')}</span>
                  </div>
                  <div className="font-serif text-2xl md:text-3xl mt-3 leading-tight">{a.name}</div>
                  <div className="mt-2 grid md:grid-cols-2 gap-2 text-sm">
                    <div><span className="text-fg/50">Email · </span><a href={`mailto:${a.email}`} className="link-underline">{a.email}</a></div>
                    {a.phone && <div><span className="text-fg/50">Tél. · </span>{a.phone}</div>}
                    {a.country && <div><span className="text-fg/50">Pays · </span>{a.country}</div>}
                    {a.discipline && <div><span className="text-fg/50">Discipline · </span>{a.discipline}</div>}
                    {a.portfolio && <div className="md:col-span-2"><span className="text-fg/50">Portfolio · </span><a href={a.portfolio} target="_blank" rel="noreferrer" className="link-underline">{a.portfolio}</a></div>}
                  </div>
                  {a.project && (
                    <p className="mt-4 text-fg/85 leading-relaxed whitespace-pre-wrap border-t border-fg/10 pt-4">{a.project}</p>
                  )}
                </div>
              );
            })}
            {apps.length === 0 && <div className="py-8 text-fg/50">Aucune candidature pour l'instant.</div>}
          </div>
        )}
        {tab === "mecenat" && mec && (
          <div className="space-y-10" data-testid="admin-mecenat">
            {/* KPI cards */}
            <div className="grid grid-cols-3 gap-px bg-fg/15 border border-fg/15">
              {[
                { k: "total", l: "Total téléchargements", v: mec.total },
                { k: "7d", l: "Derniers 7 jours", v: mec.last_7_days },
                { k: "30d", l: "Derniers 30 jours", v: mec.last_30_days },
              ].map(({ k, l, v }) => (
                <div key={k} className="bg-bg p-6" data-testid={`mec-kpi-${k}`}>
                  <div className="flex items-center justify-between">
                    <div className="meta-label text-fg/60">{l}</div>
                    <Download size={16} className="text-cinabre" />
                  </div>
                  <div className="font-serif text-5xl mt-3">{v}</div>
                </div>
              ))}
            </div>

            {/* 30-day bar chart */}
            <div>
              <div className="meta-label text-cinabre">§ Téléchargements · 30 derniers jours</div>
              <div className="mt-6 flex items-end gap-[3px] h-32 border-b border-fg/15">
                {(() => {
                  const days = [];
                  const now = new Date();
                  for (let i = 29; i >= 0; i--) {
                    const d = new Date(now);
                    d.setDate(d.getDate() - i);
                    const key = d.toISOString().slice(0, 10);
                    days.push({ key, count: mec.by_day?.[key] || 0, label: d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }) });
                  }
                  const max = Math.max(1, ...days.map(d => d.count));
                  return days.map((d) => (
                    <div
                      key={d.key}
                      className={`flex-1 transition-colors ${d.count > 0 ? "bg-cinabre hover:bg-fg" : "bg-fg/10 hover:bg-fg/30"}`}
                      style={{ height: `${Math.max(2, (d.count / max) * 100)}%` }}
                      title={`${d.label} · ${d.count} téléchargement${d.count > 1 ? "s" : ""}`}
                    />
                  ));
                })()}
              </div>
              <div className="mt-2 flex justify-between font-mono text-[9px] tracking-[0.2em] uppercase text-fg/40">
                <span>-30j</span>
                <span>aujourd'hui</span>
              </div>
            </div>

            {/* Recent downloads table */}
            <div>
              <div className="meta-label text-cinabre">§ 30 derniers téléchargements</div>
              <table className="w-full text-sm mt-6" data-testid="admin-mec-table">
                <thead>
                  <tr className="border-b border-fg/20 text-left">
                    <th className="py-3 meta-label text-fg/60">Date · Heure</th>
                    <th className="py-3 meta-label text-fg/60">IP (hash)</th>
                    <th className="py-3 meta-label text-fg/60">Navigateur</th>
                    <th className="py-3 meta-label text-fg/60">Provenance</th>
                  </tr>
                </thead>
                <tbody>
                  {mec.recent.map((d, i) => (
                    <tr key={i} className="border-b border-fg/10">
                      <td className="py-3 font-mono text-xs">{d.created_at?.slice(0, 16).replace("T", " ")}</td>
                      <td className="py-3 font-mono text-xs text-fg/60">{d.ip_hash || "—"}</td>
                      <td className="py-3 text-fg/80 max-w-xs truncate" title={d.user_agent}>{(d.user_agent || "").split(") ")[0].split("(")[0].trim() || "—"}</td>
                      <td className="py-3 text-fg/60 max-w-xs truncate" title={d.referer}>{d.referer ? new URL(d.referer).hostname : "direct"}</td>
                    </tr>
                  ))}
                  {mec.recent.length === 0 && <tr><td colSpan={4} className="py-8 text-fg/50">Aucun téléchargement enregistré pour l'instant.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {tab === "downloads" && (
          <div className="space-y-6" data-testid="admin-downloads">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="meta-label text-cinabre">§ Documents · demandes de téléchargement</div>
                <h2 className="font-serif text-3xl md:text-4xl mt-2 tracking-tight">Qui télécharge quoi</h2>
                <p className="mt-2 text-sm text-fg/60">
                  {downloads.filter(d => d.verified).length} vérifiés · {downloads.filter(d => !d.verified).length} en attente · {downloads.length} au total
                </p>
              </div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/download-requests/export.csv`}
                onClick={async (e) => {
                  e.preventDefault();
                  const token = localStorage.getItem("token");
                  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/download-requests/export.csv`, { headers: { Authorization: `Bearer ${token}` } });
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = window.document.createElement("a");
                  a.href = url; a.download = "download_requests.csv"; a.click();
                  URL.revokeObjectURL(url);
                }}
                className="btn-editorial border-cinabre text-cinabre hover:bg-cinabre hover:text-bg"
                data-testid="export-downloads-csv"
              >
                Exporter CSV
              </a>
            </div>

            {/* Filters */}
            <div className="grid md:grid-cols-3 gap-4 border border-fg/10 p-4 bg-fg/[0.02]">
              <label className="block">
                <span className="meta-label text-fg/60">Document</span>
                <select
                  value={dlFilter.doc}
                  onChange={(e) => setDlFilter({ ...dlFilter, doc: e.target.value })}
                  className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 focus:outline-none focus:border-cinabre"
                  data-testid="dl-filter-doc"
                >
                  <option value="all">Tous les documents</option>
                  <option value="mecenat">Dossier de mécénat</option>
                  <option value="statuts">Statuts</option>
                  <option value="joafe">Déclaration JOAFE</option>
                </select>
              </label>
              <label className="block">
                <span className="meta-label text-fg/60">Statut</span>
                <select
                  value={dlFilter.verified}
                  onChange={(e) => setDlFilter({ ...dlFilter, verified: e.target.value })}
                  className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 focus:outline-none focus:border-cinabre"
                  data-testid="dl-filter-verified"
                >
                  <option value="all">Tous</option>
                  <option value="verified">Vérifiés</option>
                  <option value="pending">En attente</option>
                </select>
              </label>
              <label className="block">
                <span className="meta-label text-fg/60">Rechercher (nom · email · organisation)</span>
                <input
                  type="search"
                  value={dlFilter.search}
                  onChange={(e) => setDlFilter({ ...dlFilter, search: e.target.value })}
                  placeholder="Ex. martin, acme, gmail…"
                  className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 focus:outline-none focus:border-cinabre"
                  data-testid="dl-filter-search"
                />
              </label>
            </div>

            <div className="border border-fg/10 overflow-x-auto">
              <table className="w-full text-sm" data-testid="downloads-table">
                <thead className="bg-fg/[0.03]">
                  <tr className="text-left font-mono text-[10px] tracking-[0.2em] uppercase text-fg/60">
                    <th className="p-3">Date</th>
                    <th className="p-3">Statut</th>
                    <th className="p-3">Document</th>
                    <th className="p-3">Nom</th>
                    <th className="p-3">Email</th>
                    <th className="p-3">Organisation · Fonction</th>
                    <th className="p-3">Objet</th>
                  </tr>
                </thead>
                <tbody>
                  {downloads
                    .filter(d => dlFilter.doc === "all" || d.document === dlFilter.doc)
                    .filter(d => dlFilter.verified === "all" || (dlFilter.verified === "verified" ? d.verified : !d.verified))
                    .filter(d => {
                      const q = dlFilter.search.toLowerCase().trim();
                      if (!q) return true;
                      return [d.name, d.email, d.organization, d.role].some(v => (v || "").toLowerCase().includes(q));
                    })
                    .map(d => (
                    <tr key={d.id} className="border-t border-fg/10 hover:bg-fg/[0.02] align-top">
                      <td className="p-3 font-mono text-[11px] text-fg/70 whitespace-nowrap">
                        {d.created_at?.slice(0, 16).replace("T", " ")}
                      </td>
                      <td className="p-3">
                        {d.verified ? (
                          <span className="inline-block px-2 py-0.5 bg-cinabre/10 text-cinabre font-mono text-[9px] tracking-[0.2em] uppercase">Vérifié</span>
                        ) : (
                          <span className="inline-block px-2 py-0.5 bg-fg/5 text-fg/60 font-mono text-[9px] tracking-[0.2em] uppercase">En attente</span>
                        )}
                        {d.fast_tracked_from && <div className="mt-1 font-mono text-[9px] tracking-[0.2em] uppercase text-fg/40">Fast-track</div>}
                      </td>
                      <td className="p-3">
                        <div className="font-serif">{d.document_label}</div>
                        <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-fg/45">{d.document}</div>
                      </td>
                      <td className="p-3 font-serif">{d.name}</td>
                      <td className="p-3"><a href={`mailto:${d.email}`} className="text-fg/85 hover:text-cinabre">{d.email}</a></td>
                      <td className="p-3">
                        <div className="font-serif">{d.organization || <span className="text-fg/40">—</span>}</div>
                        {d.role && <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-fg/50">{d.role}</div>}
                        {d.phone && <div className="font-mono text-[10px] text-fg/60 mt-1">{d.phone}</div>}
                      </td>
                      <td className="p-3 text-fg/75 max-w-xs">{d.purpose || <span className="text-fg/40">—</span>}</td>
                    </tr>
                  ))}
                  {downloads.length === 0 && (
                    <tr><td colSpan={7} className="p-6 text-center text-fg/50">Aucune demande de téléchargement pour le moment.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "lettres" && (
          <LettresPanel
            lettres={lettres}
            audiences={audiences}
            draft={lettreDraft}
            setDraft={setLettreDraft}
            onSaved={loadLettres}
          />
        )}

        {tab === "carnet" && (
          <CarnetPanel
            posts={blogPosts}
            audiences={audiences}
            draft={blogDraft}
            setDraft={setBlogDraft}
            onSaved={loadBlog}
          />
        )}

        {tab === "comments" && (
          <CommentsPanel items={comments} onSaved={loadComments} />
        )}

        {tab === "rencontres" && (
          <RencontresPanel
            items={rencontres}
            draft={rDraft}
            setDraft={setRDraft}
            onSaved={loadRencontres}
          />
        )}

        {tab === "ai" && (
          <AiAssistantPanel />
        )}

        {tab === "prospects" && (
          <div className="space-y-6" data-testid="admin-prospects">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="meta-label text-cinabre">§ CRM Mécénat</div>
                <h2 className="font-serif text-3xl md:text-4xl mt-2 tracking-tight">Suivi des prospects · Top 5 & au-delà</h2>
                <p className="mt-2 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50">
                  {prospects.filter(p => p.status === "engage").length} engagés · {prospects.filter(p => p.status === "a_contacter").length} à contacter · {prospects.length} au total
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/prospects.csv`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-editorial border-fg/40 hover:bg-cinabre hover:text-bg hover:border-cinabre"
                  data-testid="export-prospects-csv"
                  onClick={async (e) => {
                    // Trigger auth-protected download via fetch to include Bearer token
                    e.preventDefault();
                    const token = localStorage.getItem("film_token");
                    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/prospects.csv`, { headers: { Authorization: `Bearer ${token}` } });
                    const blob = await res.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `Prospection-Mecenat_${new Date().toISOString().slice(0,10)}.csv`;
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                  }}
                >
                  <Download size={14} /> Exporter en Excel (.csv)
                </a>
                <button
                  type="button"
                  onClick={addProspect}
                  className="btn-editorial"
                  data-testid="add-prospect"
                >
                  <Plus size={14} /> Ajouter un prospect
                </button>
              </div>
            </div>

            <div className="overflow-x-auto border border-fg/15">
              <table className="w-full text-sm" data-testid="prospects-table">
                <thead className="bg-fg/[0.03]">
                  <tr className="text-left border-b border-fg/15">
                    <th className="py-3 px-3 meta-label text-fg/60 w-10">§</th>
                    <th className="py-3 px-3 meta-label text-fg/60">Fondation / Entreprise</th>
                    <th className="py-3 px-3 meta-label text-fg/60">Catégorie</th>
                    <th className="py-3 px-3 meta-label text-fg/60">Statut</th>
                    <th className="py-3 px-3 meta-label text-fg/60">Dernier contact</th>
                    <th className="py-3 px-3 meta-label text-fg/60">Prochaine action</th>
                    <th className="py-3 px-3 meta-label text-fg/60">Notes</th>
                    <th className="py-3 px-3 meta-label text-fg/60 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {prospects.map((p) => {
                    const s = PROSPECT_STATUSES.find(x => x.k === p.status) || PROSPECT_STATUSES[0];
                    const logo = p.domain ? `https://icons.duckduckgo.com/ip3/${p.domain}.ico` : null;
                    return (
                      <tr key={p.id} className="border-b border-fg/10 hover:bg-fg/[0.02]" data-testid={`prospect-${p.id}`}>
                        <td className="px-3 py-3 font-mono text-[10px] text-fg/50 align-top">{String(p.priority || "").padStart(2, "0")}</td>
                        <td className="px-3 py-3 align-top">
                          <div className="flex items-center gap-3">
                            {logo && (
                              <span className="w-8 h-8 border border-fg/15 flex items-center justify-center bg-fg/[0.03] shrink-0">
                                <img src={logo} alt="" className="w-6 h-6 object-contain" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                              </span>
                            )}
                            <div>
                              <div className="font-serif text-base leading-tight">{p.name}</div>
                              {p.domain && <div className="font-mono text-[9px] text-fg/40 mt-0.5">{p.domain}</div>}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 font-mono text-[10px] tracking-[0.2em] uppercase text-fg/60 align-top">{p.category}</td>
                        <td className="px-3 py-3 align-top">
                          <select
                            value={p.status}
                            onChange={(e) => patchProspect(p.id, { status: e.target.value })}
                            className={`px-2 py-1 rounded text-xs font-mono uppercase tracking-widest border-0 ${s.cls} cursor-pointer`}
                            data-testid={`status-${p.id}`}
                          >
                            {PROSPECT_STATUSES.map((st) => (
                              <option key={st.k} value={st.k}>{st.l}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-3 align-top">
                          <input
                            type="date"
                            value={p.last_contact ? p.last_contact.slice(0, 10) : ""}
                            onChange={(e) => patchProspect(p.id, { last_contact: e.target.value || null })}
                            className="w-32 border-b border-fg/15 bg-transparent focus:border-cinabre outline-none text-xs py-1"
                          />
                        </td>
                        <td className="px-3 py-3 align-top">
                          <input
                            type="text"
                            defaultValue={p.next_action || ""}
                            onBlur={(e) => { if (e.target.value !== (p.next_action || "")) patchProspect(p.id, { next_action: e.target.value }); }}
                            placeholder="—"
                            className="w-40 border-b border-fg/15 bg-transparent focus:border-cinabre outline-none text-xs py-1"
                          />
                        </td>
                        <td className="px-3 py-3 align-top">
                          <input
                            type="text"
                            defaultValue={p.notes || ""}
                            onBlur={(e) => { if (e.target.value !== (p.notes || "")) patchProspect(p.id, { notes: e.target.value }); }}
                            placeholder="—"
                            className="w-52 border-b border-fg/15 bg-transparent focus:border-cinabre outline-none text-xs py-1"
                          />
                        </td>
                        <td className="px-3 py-3 align-top">
                          <button
                            type="button"
                            onClick={() => removeProspect(p.id, p.name)}
                            className="text-fg/40 hover:text-red-600 transition-colors"
                            aria-label={`Supprimer ${p.name}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {prospects.length === 0 && (
                    <tr><td colSpan={8} className="py-10 text-center text-fg/50">Aucun prospect pour l'instant.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50">
              Astuce · les modifications sont sauvegardées automatiquement (statut : au changement · autres champs : à la sortie du champ).
            </p>
          </div>
        )}
      </div>
    </main>
  );
}


// ─── Lettres du Fil · éditorial signé par « La Fée du canal » ────────────────
function LettresPanel({ lettres, audiences, draft, setDraft, onSaved }) {
  const isEditing = Boolean(draft.id);

  // Relance 2026
  const [relance, setRelance] = useState({ total: 0, with_email: 0, without_email: 0, recipients: [] });
  const [showRelanceList, setShowRelanceList] = useState(false);
  const [emailDraft, setEmailDraft] = useState({});
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [csvResult, setCsvResult] = useState(null);
  const loadRelance = () => api.get("/admin/relance/recipients").then(r => setRelance(r.data)).catch(() => {});
  useEffect(() => { loadRelance(); }, []);

  const importCsv = async () => {
    if (!csvText.trim()) return toast.error("Collez d'abord votre tableau.");
    try {
      const { data } = await api.post("/admin/relance/import-emails", { csv: csvText });
      setCsvResult(data);
      toast.success(`${data.updated} email(s) attaché(s) · ${data.unmatched.length} non trouvé(s).`);
      loadRelance();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import impossible.");
    }
  };

  const saveEmail = async (name) => {
    const email = (emailDraft[name] || "").trim();
    if (!email) return;
    try {
      await api.post("/admin/relance/set-email", { name, email });
      toast.success(`Email enregistré pour ${name}.`);
      setEmailDraft((d) => { const n = { ...d }; delete n[name]; return n; });
      loadRelance();
    } catch (err) {
      toast.error("Enregistrement impossible.");
    }
  };

  const sendRelance = async () => {
    if (relance.with_email === 0) return toast.error("Aucun destinataire n'a d'email — complétez d'abord la liste.");
    if (!window.confirm(`Envoyer le courrier de rappel à ${relance.with_email} destinataire${relance.with_email > 1 ? "s" : ""} ? (Nécessite la clé email active.)`)) return;
    try {
      const { data } = await api.post("/admin/relance/send");
      if (data.sent > 0) toast.success(`Courrier envoyé à ${data.sent} destinataire(s).`);
      else toast.error("Aucun envoi n'a abouti (clé email en 401 ?).");
      onSaved?.();
      loadRelance();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi impossible.");
    }
  };

  const totalRecipients = audiences
    .filter((a) => draft.segments.includes(a.key))
    .reduce((n, a) => n + a.count, 0);

  const toggleSegment = (key) => {
    setDraft({
      ...draft,
      segments: draft.segments.includes(key)
        ? draft.segments.filter((s) => s !== key)
        : [...draft.segments, key],
    });
  };

  const resetDraft = () => setDraft({
    id: null, title: "", month_label: "", hero_image: "", artist_name: "",
    escale_name: "", atelier_photo: "", body_html: "", signature: "La Fée du canal",
    segments: [],
  });

  const save = async () => {
    if (!draft.title.trim()) return toast.error("Un titre est nécessaire.");
    try {
      const payload = { ...draft }; delete payload.id;
      if (isEditing) {
        await api.patch(`/admin/lettres/${draft.id}`, payload);
        toast.success("Lettre mise à jour.");
      } else {
        await api.post("/admin/lettres", payload);
        toast.success("Lettre enregistrée en brouillon.");
      }
      resetDraft();
      onSaved?.();
    } catch (err) {
      toast.error("Sauvegarde impossible. Vérifiez la connexion.");
    }
  };

  const send = async (id) => {
    if (!window.confirm("Envoyer cette lettre aux segments sélectionnés ? (Nécessite que la clé email soit active.)")) return;
    try {
      const { data } = await api.post(`/admin/lettres/${id}/send`);
      if (data.sent > 0) toast.success(`Envoyée à ${data.sent} destinataire(s).`);
      else toast.error("Aucun envoi n'a abouti (clé email en 401 ?)." );
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi impossible.");
    }
  };

  const preview = async (id) => {
    try {
      const { data } = await api.post(`/admin/lettres/${id}/preview`);
      if (data.ok) toast.success(`Aperçu envoyé à ${data.recipient}.`);
      else toast.error("Aperçu non envoyé (clé email en 401 ?)." );
    } catch (err) {
      toast.error("Aperçu impossible.");
    }
  };

  const editLettre = (l) => setDraft({
    id: l.id, title: l.title || "", month_label: l.month_label || "",
    hero_image: l.hero_image || "", artist_name: l.artist_name || "",
    escale_name: l.escale_name || "", atelier_photo: l.atelier_photo || "",
    body_html: l.body_html || "", signature: l.signature || "La Fée du canal",
    segments: l.segments || [],
  });

  const removeLettre = async (id) => {
    if (!window.confirm("Supprimer définitivement cette lettre ?")) return;
    try {
      await api.delete(`/admin/lettres/${id}`);
      onSaved?.();
      if (draft.id === id) resetDraft();
      toast.success("Lettre supprimée.");
    } catch { toast.error("Suppression impossible."); }
  };

  return (
    <div className="space-y-8" data-testid="admin-lettres">
      <div>
        <div className="meta-label text-cinabre">§ Newsletter éditoriale · Segmentée par contribution</div>
        <h2 className="font-serif text-3xl md:text-4xl mt-2 tracking-tight">Lettres du Fil</h2>
        <p className="mt-2 text-sm text-fg/60 max-w-2xl">
          Une lettre mensuelle signée par <em>la Fée du canal</em> — une œuvre du mois, une escale à venir, une photo d'atelier. Choisissez les segments qui la reçoivent ; chaque mécène devient une fée du parcours.
        </p>
      </div>

      {/* Relance 2026 · Rappel aux anciens exposants + pré-inscrits verbaux */}
      <div className="border-2 border-cinabre/40 bg-cinabre/[0.03] p-6 md:p-8" data-testid="relance-2026">
        <div className="grid md:grid-cols-12 gap-6 items-start">
          <div className="md:col-span-8">
            <div className="meta-label text-cinabre">§ Relance 2026 · Anciens exposants + Pré-inscrits verbaux</div>
            <h3 className="mt-3 font-serif text-2xl md:text-3xl tracking-tight leading-tight">
              Formaliser leur inscription 2026.
            </h3>
            <p className="mt-3 text-sm text-fg/75 leading-relaxed max-w-xl">
              Un courrier signé par <em>La Fée du canal</em>, avec un bouton direct vers le formulaire d'inscription. Envoyé en un clic à tous les artistes qui ont participé aux éditions précédentes ou donné leur accord verbal.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right space-y-1">
            <div className="font-serif font-light text-5xl md:text-6xl tabular-nums leading-none text-cinabre">
              {relance.total}
            </div>
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">
              destinataires uniques
            </div>
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50 pt-1">
              <span className="text-emerald-700">{relance.with_email} avec email</span> · {relance.without_email} à recontacter
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-4 flex-wrap border-t border-cinabre/20 pt-5">
          <button onClick={sendRelance} disabled={relance.with_email === 0} className="btn-editorial disabled:opacity-40 disabled:cursor-not-allowed" data-testid="relance-send">
            Envoyer le courrier de rappel <Send size={14} />
          </button>
          <button onClick={() => setCsvOpen(!csvOpen)} className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre hover:underline" data-testid="relance-toggle-csv">
            {csvOpen ? "Fermer l'import" : "Importer CSV"}
          </button>
          <button onClick={() => setShowRelanceList(!showRelanceList)} className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre hover:underline" data-testid="relance-toggle-list">
            {showRelanceList ? "Masquer la liste" : `Ajouter emails 1 à 1 (${relance.without_email})`}
          </button>
          {relance.with_email === 0 && (
            <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50">
              § Aucun email disponible — complétez la liste
            </span>
          )}
        </div>

        {csvOpen && (
          <div className="mt-6 border-t border-cinabre/20 pt-5 space-y-3" data-testid="relance-csv">
            <div className="meta-label text-fg/60">§ Import en masse · Collez un tableau <span className="text-cinabre">Nom, Email</span> (une ligne par artiste)</div>
            <textarea
              rows={8}
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              placeholder={`Michel Batlle, michel.batlle@example.com\nDenis Cat, denis@example.com\nKossi Homawoo, kossi@example.com\n...`}
              className="w-full border border-fg/20 bg-bg p-4 font-mono text-xs leading-relaxed focus:outline-none focus:border-cinabre resize-vertical"
              data-testid="relance-csv-input"
            />
            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={importCsv} disabled={!csvText.trim()} className="btn-editorial disabled:opacity-40" data-testid="relance-csv-import">
                Importer les emails
              </button>
              <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50">
                Séparateur détecté auto : virgule, point-virgule ou tabulation
              </span>
            </div>
            {csvResult && (
              <div className="mt-3 border-t border-fg/10 pt-3 text-sm space-y-1">
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-emerald-700">
                  ✓ {csvResult.updated} email{csvResult.updated > 1 ? "s" : ""} attaché{csvResult.updated > 1 ? "s" : ""}
                </div>
                {csvResult.unmatched?.length > 0 && (
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-amber-700">
                    ⚠ {csvResult.unmatched.length} nom{csvResult.unmatched.length > 1 ? "s" : ""} non trouvé{csvResult.unmatched.length > 1 ? "s" : ""} : <span className="font-serif normal-case text-fg/70">{csvResult.unmatched.join(", ")}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {showRelanceList && (
          <div className="mt-6 border-t border-cinabre/20 pt-5 space-y-2 max-h-[400px] overflow-y-auto pr-2" data-testid="relance-list">
            {relance.recipients.map((r) => (
              <div key={r.name} className="flex items-center gap-3 flex-wrap py-2 border-b border-fg/10">
                <div className="flex-1 min-w-[200px]">
                  <div className="font-serif text-base leading-tight">{r.name}</div>
                  <div className="font-mono text-[9px] tracking-[0.24em] uppercase text-fg/55 mt-0.5">
                    {r.country || "—"} · {r.kinds.join(", ")}
                  </div>
                </div>
                {r.email ? (
                  <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-emerald-700">
                    ✓ {r.email}
                  </span>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      type="email"
                      value={emailDraft[r.name] || ""}
                      onChange={(e) => setEmailDraft({ ...emailDraft, [r.name]: e.target.value })}
                      onKeyDown={(e) => e.key === "Enter" && saveEmail(r.name)}
                      placeholder="email…"
                      className="border-b border-fg/25 bg-transparent py-1 px-2 font-mono text-xs w-56 focus:outline-none focus:border-cinabre"
                    />
                    <button
                      onClick={() => saveEmail(r.name)}
                      className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre hover:underline"
                    >
                      Enregistrer
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Composer */}
        <div className="lg:col-span-7 space-y-5 border border-fg/15 p-6 md:p-8 bg-fg/[0.02]" data-testid="lettre-composer">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-cinabre" />
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">
              {isEditing ? "Modifier la lettre" : "Composer une nouvelle lettre"}
            </div>
          </div>

          <label className="block">
            <span className="meta-label text-fg/60">Titre *</span>
            <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="Ex. Février · La cueillette d'œuvres"
              className="mt-2 w-full border-b border-fg/30 bg-transparent py-3 font-serif text-xl focus:outline-none focus:border-cinabre"
              data-testid="lettre-title" />
          </label>

          <div className="grid md:grid-cols-2 gap-5">
            <label className="block">
              <span className="meta-label text-fg/60">Mois</span>
              <input value={draft.month_label} onChange={(e) => setDraft({ ...draft, month_label: e.target.value })}
                placeholder="Février 2026"
                className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 focus:outline-none focus:border-cinabre"
                data-testid="lettre-month" />
            </label>
            <label className="block">
              <span className="meta-label text-fg/60">Signature</span>
              <input value={draft.signature} onChange={(e) => setDraft({ ...draft, signature: e.target.value })}
                className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 focus:outline-none focus:border-cinabre"
                data-testid="lettre-signature" />
            </label>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <label className="block">
              <span className="meta-label text-fg/60">Œuvre du mois — artiste</span>
              <input value={draft.artist_name} onChange={(e) => setDraft({ ...draft, artist_name: e.target.value })}
                placeholder="Denis Cat"
                className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 focus:outline-none focus:border-cinabre"
                data-testid="lettre-artist" />
            </label>
            <label className="block">
              <span className="meta-label text-fg/60">Escale à venir</span>
              <input value={draft.escale_name} onChange={(e) => setDraft({ ...draft, escale_name: e.target.value })}
                placeholder="Malpas · avril 2026"
                className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 focus:outline-none focus:border-cinabre"
                data-testid="lettre-escale" />
            </label>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <label className="block">
              <span className="meta-label text-fg/60">Image d'en-tête (URL)</span>
              <input value={draft.hero_image} onChange={(e) => setDraft({ ...draft, hero_image: e.target.value })}
                placeholder="https://…"
                className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 font-mono text-xs focus:outline-none focus:border-cinabre"
                data-testid="lettre-hero" />
            </label>
            <label className="block">
              <span className="meta-label text-fg/60">Photo d'atelier (URL)</span>
              <input value={draft.atelier_photo} onChange={(e) => setDraft({ ...draft, atelier_photo: e.target.value })}
                placeholder="https://…"
                className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 font-mono text-xs focus:outline-none focus:border-cinabre"
                data-testid="lettre-atelier" />
            </label>
          </div>

          <label className="block">
            <span className="meta-label text-fg/60">Corps de la lettre (HTML autorisé)</span>
            <textarea rows={8} value={draft.body_html} onChange={(e) => setDraft({ ...draft, body_html: e.target.value })}
              placeholder="<p>Le canal se réveille…</p>"
              className="mt-2 w-full border border-fg/20 bg-transparent p-4 font-serif text-base leading-relaxed focus:outline-none focus:border-cinabre resize-vertical"
              data-testid="lettre-body" />
          </label>

          {/* Segments */}
          <div>
            <div className="meta-label text-fg/60 mb-3">§ Destinataires · segments</div>
            <div className="flex flex-wrap gap-2">
              {audiences.map((a) => {
                const active = draft.segments.includes(a.key);
                return (
                  <button
                    key={a.key}
                    type="button"
                    onClick={() => toggleSegment(a.key)}
                    className={`px-3.5 py-1.5 text-[13px] font-serif border transition-all ${active ? "bg-cinabre text-bg border-cinabre" : "bg-transparent text-fg/80 border-fg/25 hover:border-cinabre hover:text-cinabre"}`}
                    data-testid={`lettre-segment-${a.key}`}
                  >
                    {a.label} · <span className="font-mono text-[11px] opacity-80">{a.count}</span>
                  </button>
                );
              })}
            </div>
            <div className="mt-3 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">
              Audience totale (dédoublonnée par email) : <span className="text-cinabre">≈ {totalRecipients}</span>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 pt-4 border-t border-fg/10">
            <button onClick={resetDraft} className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60 hover:text-cinabre" data-testid="lettre-reset">
              {isEditing ? "Annuler l'édition" : "Vider le formulaire"}
            </button>
            <button onClick={save} className="btn-editorial" data-testid="lettre-save">
              {isEditing ? "Mettre à jour" : "Enregistrer le brouillon"} <Send size={14} />
            </button>
          </div>
        </div>

        {/* List */}
        <div className="lg:col-span-5 space-y-4" data-testid="lettre-list">
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">
            § Historique · {lettres.length} lettre{lettres.length > 1 ? "s" : ""}
          </div>
          {lettres.length === 0 && (
            <div className="border border-dashed border-fg/20 p-6 text-center text-sm text-fg/50">
              Aucune lettre pour l'instant. Composez la première à gauche.
            </div>
          )}
          {lettres.map((l) => (
            <div key={l.id} className="border border-fg/15 p-4 md:p-5 space-y-2" data-testid={`lettre-item-${l.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-serif text-lg leading-tight">{l.title}</div>
                  <div className="mt-1 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50">
                    {l.month_label || "—"} · {l.segments.length} segment{l.segments.length > 1 ? "s" : ""}
                  </div>
                </div>
                <StatusPill status={l.status} sent={l.recipients_count} />
              </div>
              <div className="flex flex-wrap gap-1">
                {l.segments.map((s) => (
                  <span key={s} className="px-2 py-0.5 bg-fg/5 font-mono text-[9px] tracking-[0.2em] uppercase text-fg/60">{s}</span>
                ))}
              </div>
              <div className="flex items-center gap-3 pt-2 border-t border-fg/10">
                <button onClick={() => editLettre(l)} className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/70 hover:text-cinabre" data-testid={`lettre-edit-${l.id}`}>
                  Modifier
                </button>
                <button onClick={() => preview(l.id)} className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/70 hover:text-cinabre inline-flex items-center gap-1" data-testid={`lettre-preview-${l.id}`}>
                  <Eye size={11} /> Aperçu (admin)
                </button>
                <button onClick={() => send(l.id)} className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre hover:underline inline-flex items-center gap-1" data-testid={`lettre-send-${l.id}`}>
                  <Send size={11} /> Envoyer
                </button>
                <button onClick={() => removeLettre(l.id)} className="ml-auto font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50 hover:text-red-600" data-testid={`lettre-delete-${l.id}`}>
                  <Trash2 size={11} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50 pt-4 border-t border-fg/10">
        Astuce · tant que la clé email Emergent est en 401, les envois échoueront — les brouillons restent prêts à partir.
      </p>
    </div>
  );
}

function StatusPill({ status, sent }) {
  const map = {
    draft:   { l: "Brouillon", cls: "bg-fg/5 text-fg/60" },
    sent:    { l: `Envoyée · ${sent}`, cls: "bg-emerald-500/15 text-emerald-700" },
    partial: { l: `Partielle · ${sent}`, cls: "bg-amber-500/15 text-amber-700" },
    failed:  { l: "Échec envoi", cls: "bg-red-500/15 text-red-700" },
  };
  const p = map[status] || map.draft;
  return (
    <span className={`inline-block px-2 py-0.5 font-mono text-[9px] tracking-[0.2em] uppercase whitespace-nowrap ${p.cls}`}>
      {p.l}
    </span>
  );
}


// ─── Le Carnet · Blog éditorial ──────────────────────────────────────────────
function CarnetPanel({ posts, audiences = [], draft, setDraft, onSaved }) {
  const isEditing = Boolean(draft.id);
  const [broadcastFor, setBroadcastFor] = useState(null); // post being broadcast
  const [broadcastSegments, setBroadcastSegments] = useState([]);
  const [broadcastSending, setBroadcastSending] = useState(false);

  const resetDraft = () => setDraft({
    id: null, slug: "", title: "", subtitle: "", hero_image: "", hero_caption: "",
    author: "Les Arteurs", excerpt: "", body_html: "", tags: [], status: "draft",
  });

  const editPost = (p) => setDraft({
    id: p.id, slug: p.slug || "", title: p.title || "", subtitle: p.subtitle || "",
    hero_image: p.hero_image || "", hero_caption: p.hero_caption || "",
    author: p.author || "Les Arteurs", excerpt: p.excerpt || "",
    body_html: p.body_html || "", tags: p.tags || [], status: p.status || "draft",
  });

  const save = async (publish = false) => {
    if (!draft.title.trim()) return toast.error("Un titre est nécessaire.");
    const payload = { ...draft };
    delete payload.id;
    payload.status = publish ? "published" : (draft.status || "draft");
    try {
      if (isEditing) {
        await api.patch(`/admin/blog/${draft.id}`, payload);
        toast.success(publish ? "Article publié." : "Article mis à jour.");
      } else {
        await api.post("/admin/blog", payload);
        toast.success(publish ? "Article publié." : "Brouillon enregistré.");
      }
      resetDraft();
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sauvegarde impossible.");
    }
  };

  const removePost = async (id) => {
    if (!window.confirm("Supprimer définitivement cet article ?")) return;
    try {
      await api.delete(`/admin/blog/${id}`);
      onSaved?.();
      if (draft.id === id) resetDraft();
      toast.success("Article supprimé.");
    } catch { toast.error("Suppression impossible."); }
  };

  const tagsInput = draft.tags.join(", ");
  const onTagsChange = (e) => setDraft({ ...draft, tags: e.target.value.split(",").map(t => t.trim()).filter(Boolean) });

  const openBroadcast = (post) => {
    setBroadcastFor(post);
    setBroadcastSegments([]);
  };
  const closeBroadcast = () => {
    setBroadcastFor(null);
    setBroadcastSegments([]);
  };
  const toggleBcSeg = (k) => setBroadcastSegments((cur) => cur.includes(k) ? cur.filter(s => s !== k) : [...cur, k]);
  const totalBcRecipients = audiences.filter((a) => broadcastSegments.includes(a.key)).reduce((n, a) => n + a.count, 0);

  const sendBroadcast = async () => {
    if (!broadcastFor) return;
    if (broadcastSegments.length === 0) return toast.error("Sélectionnez au moins un segment.");
    if (!window.confirm(`Envoyer cette chronique à ≈ ${totalBcRecipients} destinataire(s) ? (Nécessite que la clé email soit active.)`)) return;
    setBroadcastSending(true);
    try {
      const { data } = await api.post(`/admin/blog/${broadcastFor.id}/broadcast`, { segments: broadcastSegments });
      if (data.sent > 0) toast.success(`Envoyée à ${data.sent} destinataire(s).`);
      else toast.error("Aucun envoi n'a abouti (clé email en 401 ?).");
      closeBroadcast();
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi impossible.");
    } finally {
      setBroadcastSending(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-carnet">
      <div>
        <div className="meta-label text-cinabre">§ Le Carnet · Chroniques éditoriales du Fil</div>
        <h2 className="font-serif text-3xl md:text-4xl mt-2 tracking-tight">Journal du Film</h2>
        <p className="mt-2 text-sm text-fg/60 max-w-2xl">
          Un blog éditorial pour raconter le parcours — la Fée du Canal, les rencontres, les matins d'hiver. Publiez, gardez en brouillon, ou dépubliez à volonté.
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Composer */}
        <div className="lg:col-span-7 space-y-5 border border-fg/15 p-6 md:p-8 bg-fg/[0.02]" data-testid="carnet-composer">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-cinabre" />
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">
              {isEditing ? `Modifier · ${draft.status}` : "Composer une chronique"}
            </div>
          </div>

          <label className="block">
            <span className="meta-label text-fg/60">Titre *</span>
            <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="Ex. La Fée du Canal — et celles à venir"
              className="mt-2 w-full border-b border-fg/30 bg-transparent py-3 font-serif text-xl focus:outline-none focus:border-cinabre"
              data-testid="carnet-title" />
          </label>

          <label className="block">
            <span className="meta-label text-fg/60">Sous-titre</span>
            <input value={draft.subtitle} onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })}
              className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 font-serif italic text-lg focus:outline-none focus:border-cinabre"
              data-testid="carnet-subtitle" />
          </label>

          <div className="grid md:grid-cols-2 gap-5">
            <label className="block">
              <span className="meta-label text-fg/60">Slug URL (auto si vide)</span>
              <input value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
                placeholder="la-fee-du-canal"
                className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 font-mono text-sm focus:outline-none focus:border-cinabre"
                data-testid="carnet-slug" />
            </label>
            <label className="block">
              <span className="meta-label text-fg/60">Auteur</span>
              <input value={draft.author} onChange={(e) => setDraft({ ...draft, author: e.target.value })}
                className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 focus:outline-none focus:border-cinabre"
                data-testid="carnet-author" />
            </label>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <label className="block">
              <span className="meta-label text-fg/60">Image de une (URL)</span>
              <input value={draft.hero_image} onChange={(e) => setDraft({ ...draft, hero_image: e.target.value })}
                placeholder="https://…"
                className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 font-mono text-xs focus:outline-none focus:border-cinabre"
                data-testid="carnet-hero" />
            </label>
            <label className="block">
              <span className="meta-label text-fg/60">Légende de l'image</span>
              <input value={draft.hero_caption} onChange={(e) => setDraft({ ...draft, hero_caption: e.target.value })}
                className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 focus:outline-none focus:border-cinabre"
                data-testid="carnet-hero-caption" />
            </label>
          </div>

          <label className="block">
            <span className="meta-label text-fg/60">Chapô · résumé (2-3 lignes)</span>
            <textarea rows={3} value={draft.excerpt} onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })}
              className="mt-2 w-full border border-fg/20 bg-transparent p-3 font-serif italic text-base leading-relaxed focus:outline-none focus:border-cinabre resize-vertical"
              data-testid="carnet-excerpt" />
          </label>

          <label className="block">
            <span className="meta-label text-fg/60">Corps de l'article (HTML : &lt;p&gt;, &lt;h2&gt;, &lt;h3&gt;, &lt;em&gt;, &lt;strong&gt;, &lt;blockquote&gt;, &lt;ul&gt;/&lt;li&gt;, &lt;a&gt;)</span>
            <textarea rows={16} value={draft.body_html} onChange={(e) => setDraft({ ...draft, body_html: e.target.value })}
              placeholder="<p class='lead'>…</p>\n<h2>…</h2>\n<p>…</p>\n<blockquote>…<footer>— …</footer></blockquote>"
              className="mt-2 w-full border border-fg/20 bg-transparent p-4 font-mono text-xs leading-relaxed focus:outline-none focus:border-cinabre resize-vertical"
              data-testid="carnet-body" />
          </label>

          <label className="block">
            <span className="meta-label text-fg/60">Mots-clés · séparés par des virgules</span>
            <input value={tagsInput} onChange={onTagsChange}
              placeholder="Fée du Canal, Mécénat, Récit du Fil"
              className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 focus:outline-none focus:border-cinabre"
              data-testid="carnet-tags" />
          </label>

          <div className="flex items-center justify-between gap-4 pt-4 border-t border-fg/10 flex-wrap">
            <button onClick={resetDraft} className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60 hover:text-cinabre" data-testid="carnet-reset">
              {isEditing ? "Annuler l'édition" : "Vider"}
            </button>
            <div className="flex items-center gap-3">
              <button onClick={() => save(false)} className="btn-editorial ghost" data-testid="carnet-save-draft">
                Enregistrer brouillon
              </button>
              <button onClick={() => save(true)} className="btn-editorial" data-testid="carnet-publish">
                {isEditing && draft.status === "published" ? "Republier" : "Publier"} <Send size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* List */}
        <div className="lg:col-span-5 space-y-4" data-testid="carnet-list">
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">
            § Articles · {posts.length}
          </div>
          {posts.length === 0 && (
            <div className="border border-dashed border-fg/20 p-6 text-center text-sm text-fg/50">
              Aucun article. Composez le premier à gauche.
            </div>
          )}
          {posts.map((p) => (
            <div key={p.id} className="border border-fg/15 p-4 md:p-5 space-y-2" data-testid={`carnet-item-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-serif text-lg leading-tight">{p.title}</div>
                  <div className="mt-1 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50">
                    /carnet/{p.slug}
                  </div>
                </div>
                <span className={`inline-block px-2 py-0.5 font-mono text-[9px] tracking-[0.2em] uppercase whitespace-nowrap ${p.status === "published" ? "bg-emerald-500/15 text-emerald-700" : "bg-fg/5 text-fg/60"}`}>
                  {p.status === "published" ? "Publié" : "Brouillon"}
                </span>
              </div>
              {p.subtitle && <div className="font-serif italic text-sm text-fg/70 leading-snug">{p.subtitle}</div>}
              <div className="flex items-center gap-3 pt-2 border-t border-fg/10">
                <button onClick={() => editPost(p)} className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/70 hover:text-cinabre" data-testid={`carnet-edit-${p.id}`}>
                  Modifier
                </button>
                {p.status === "published" && (
                  <>
                    <a href={`/carnet/${p.slug}`} target="_blank" rel="noreferrer" className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/70 hover:text-cinabre" data-testid={`carnet-view-${p.id}`}>
                      Voir en ligne ↗
                    </a>
                    <button onClick={() => openBroadcast(p)} className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre hover:underline inline-flex items-center gap-1" data-testid={`carnet-broadcast-${p.id}`}>
                      <Send size={11} /> Envoyer aux abonnés
                    </button>
                    {p.last_broadcast_at && (
                      <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-fg/45">
                        · diffusé {p.last_broadcast_count || 0}×
                      </span>
                    )}
                  </>
                )}
                <button onClick={() => removePost(p.id)} className="ml-auto font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50 hover:text-red-600" data-testid={`carnet-delete-${p.id}`}>
                  <Trash2 size={11} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Broadcast dialog */}
      {broadcastFor && (
        <div className="fixed inset-0 z-[1000] bg-fg/60 backdrop-blur-sm flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto" onClick={closeBroadcast} data-testid="broadcast-modal">
          <div className="w-full max-w-2xl bg-bg border border-fg/15 shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={closeBroadcast} className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center hover:bg-fg hover:text-bg transition-colors" data-testid="broadcast-close">
              <Trash2 size={0} /><span className="font-mono text-lg">×</span>
            </button>
            <div className="p-8 md:p-10">
              <div className="meta-label text-cinabre">§ Diffuser · Le Carnet → Les Fées du Fil</div>
              <h3 className="font-serif text-3xl md:text-4xl mt-3 tracking-tight leading-tight">Envoyer aux abonnés</h3>
              <p className="mt-3 text-sm text-fg/70 leading-relaxed">
                Un email éditorial signé par <em>la Fée du canal</em> — le titre, le sous-titre, l'image, le chapô, et un bouton vers la chronique complète. Choisissez qui la reçoit.
              </p>

              <div className="mt-6 p-4 border border-fg/15 bg-fg/[0.03]">
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">§ Article à diffuser</div>
                <div className="mt-2 font-serif text-xl leading-tight">{broadcastFor.title}</div>
                {broadcastFor.subtitle && <div className="mt-1 font-serif italic text-sm text-fg/70">{broadcastFor.subtitle}</div>}
                <div className="mt-2 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/45">/carnet/{broadcastFor.slug}</div>
              </div>

              <div className="mt-6">
                <div className="meta-label text-fg/60 mb-3">§ Destinataires · segments</div>
                <div className="flex flex-wrap gap-2">
                  {audiences.map((a) => {
                    const active = broadcastSegments.includes(a.key);
                    return (
                      <button
                        key={a.key}
                        type="button"
                        onClick={() => toggleBcSeg(a.key)}
                        className={`px-3.5 py-1.5 text-[13px] font-serif border transition-all ${active ? "bg-cinabre text-bg border-cinabre" : "bg-transparent text-fg/80 border-fg/25 hover:border-cinabre hover:text-cinabre"}`}
                        data-testid={`broadcast-seg-${a.key}`}
                      >
                        {a.label} · <span className="font-mono text-[11px] opacity-80">{a.count}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-3 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">
                  Audience totale (dédoublonnée) : <span className="text-cinabre">≈ {totalBcRecipients}</span>
                </div>
              </div>

              <div className="mt-8 flex items-center justify-between gap-4 border-t border-fg/10 pt-6 flex-wrap">
                <button onClick={closeBroadcast} className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60 hover:text-cinabre" data-testid="broadcast-cancel">
                  Annuler
                </button>
                <button onClick={sendBroadcast} disabled={broadcastSending || broadcastSegments.length === 0} className="btn-editorial disabled:opacity-40 disabled:cursor-not-allowed" data-testid="broadcast-send">
                  {broadcastSending ? "Envoi…" : "Envoyer maintenant"} <Send size={14} />
                </button>
              </div>
              <p className="mt-4 font-mono text-[9px] tracking-[0.24em] uppercase text-fg/45 leading-relaxed">
                Note · tant que la clé email Emergent est en 401, l'envoi échouera silencieusement — l'historique enregistrera la tentative.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Rencontres du Fil · Galerie participative ──────────────────────────────
function RencontresPanel({ items, draft, setDraft, onSaved }) {
  const isEditing = Boolean(draft.id);

  const resetDraft = () => setDraft({
    id: null, image_url: "", photographer_name: "", photographer_email: "",
    location: "", taken_at: "", message: "", featured: false, status: "approved",
  });

  const editItem = (r) => setDraft({
    id: r.id, image_url: r.image_url || "", photographer_name: r.photographer_name || "",
    photographer_email: r.photographer_email || "", location: r.location || "",
    taken_at: r.taken_at || "", message: r.message || "", featured: !!r.featured,
    status: r.status || "approved",
  });

  const save = async () => {
    if (!draft.image_url.trim()) return toast.error("Une URL d'image est requise.");
    try {
      const payload = { ...draft }; delete payload.id;
      if (isEditing) {
        await api.patch(`/admin/rencontres/${draft.id}`, payload);
        toast.success("Rencontre mise à jour.");
      } else {
        await api.post("/admin/rencontres", payload);
        toast.success("Rencontre enregistrée.");
      }
      resetDraft();
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sauvegarde impossible.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette rencontre ?")) return;
    try {
      await api.delete(`/admin/rencontres/${id}`);
      onSaved?.();
      if (draft.id === id) resetDraft();
      toast.success("Rencontre supprimée.");
    } catch { toast.error("Suppression impossible."); }
  };

  return (
    <div className="space-y-8" data-testid="admin-rencontres">
      <div>
        <div className="meta-label text-cinabre">§ Concours des rencontres · Chronique visuelle collective</div>
        <h2 className="font-serif text-3xl md:text-4xl mt-2 tracking-tight">Rencontres du Fil</h2>
        <p className="mt-2 text-sm text-fg/60 max-w-2xl">
          Les photos envoyées par les promeneurs — collez l'URL d'une image reçue par email, notez le contexte, cochez « coup de cœur » pour la mettre en avant. Elles apparaîtront en frise sous l'article de la Fée du Canal.
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Composer */}
        <div className="lg:col-span-6 space-y-5 border border-fg/15 p-6 md:p-8 bg-fg/[0.02]" data-testid="rencontre-composer">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-cinabre" />
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">
              {isEditing ? "Modifier la rencontre" : "Ajouter une rencontre reçue"}
            </div>
          </div>

          <label className="block">
            <span className="meta-label text-fg/60">URL de l'image *</span>
            <input value={draft.image_url} onChange={(e) => setDraft({ ...draft, image_url: e.target.value })}
              placeholder="https://…"
              className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 font-mono text-xs focus:outline-none focus:border-cinabre"
              data-testid="rencontre-image" />
            {draft.image_url && (
              <div className="mt-3 aspect-[3/4] w-32 border border-fg/15 overflow-hidden bg-fg/5">
                <img src={draft.image_url} alt="Aperçu" className="w-full h-full object-cover" />
              </div>
            )}
          </label>

          <div className="grid md:grid-cols-2 gap-5">
            <label className="block">
              <span className="meta-label text-fg/60">Photographe · nom</span>
              <input value={draft.photographer_name} onChange={(e) => setDraft({ ...draft, photographer_name: e.target.value })}
                placeholder="Ex. Marie L."
                className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 focus:outline-none focus:border-cinabre"
                data-testid="rencontre-photographer" />
            </label>
            <label className="block">
              <span className="meta-label text-fg/60">Photographe · email (privé)</span>
              <input value={draft.photographer_email} onChange={(e) => setDraft({ ...draft, photographer_email: e.target.value })}
                type="email"
                className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 font-mono text-xs focus:outline-none focus:border-cinabre"
                data-testid="rencontre-email" />
            </label>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <label className="block">
              <span className="meta-label text-fg/60">Lieu</span>
              <input value={draft.location} onChange={(e) => setDraft({ ...draft, location: e.target.value })}
                placeholder="Ex. Pont-canal de Castelnau"
                className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 focus:outline-none focus:border-cinabre"
                data-testid="rencontre-location" />
            </label>
            <label className="block">
              <span className="meta-label text-fg/60">Moment</span>
              <input value={draft.taken_at} onChange={(e) => setDraft({ ...draft, taken_at: e.target.value })}
                placeholder="Ex. Un matin d'hiver"
                className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 focus:outline-none focus:border-cinabre"
                data-testid="rencontre-when" />
            </label>
          </div>

          <label className="block">
            <span className="meta-label text-fg/60">Petit mot (facultatif, apparaîtra en italique)</span>
            <textarea rows={3} value={draft.message} onChange={(e) => setDraft({ ...draft, message: e.target.value })}
              placeholder="Ex. Je suis passé au pont-canal, elle m'a regardé."
              className="mt-2 w-full border border-fg/20 bg-transparent p-3 font-serif italic text-base leading-relaxed focus:outline-none focus:border-cinabre resize-vertical"
              data-testid="rencontre-message" />
          </label>

          <div className="grid md:grid-cols-2 gap-5 items-center">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={draft.featured}
                onChange={(e) => setDraft({ ...draft, featured: e.target.checked })}
                className="w-4 h-4 accent-cinabre"
                data-testid="rencontre-featured" />
              <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/70">§ Coup de cœur</span>
            </label>
            <label className="block">
              <span className="meta-label text-fg/60">Statut</span>
              <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 focus:outline-none focus:border-cinabre"
                data-testid="rencontre-status">
                <option value="approved">Approuvée · visible</option>
                <option value="pending">En attente</option>
                <option value="rejected">Rejetée</option>
              </select>
            </label>
          </div>

          <div className="flex items-center justify-between gap-4 pt-4 border-t border-fg/10">
            <button onClick={resetDraft} className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60 hover:text-cinabre" data-testid="rencontre-reset">
              {isEditing ? "Annuler" : "Vider"}
            </button>
            <button onClick={save} className="btn-editorial" data-testid="rencontre-save">
              {isEditing ? "Mettre à jour" : "Ajouter à la galerie"} <Send size={14} />
            </button>
          </div>
        </div>

        {/* Grid preview */}
        <div className="lg:col-span-6 space-y-4" data-testid="rencontre-list">
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">
            § Galerie · {items.length} rencontre{items.length > 1 ? "s" : ""}
          </div>
          {items.length === 0 && (
            <div className="border border-dashed border-fg/20 p-6 text-center text-sm text-fg/50">
              Aucune photo dans la galerie. La première apparaîtra ici dès qu'un promeneur écrira.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {items.map((r) => (
              <div key={r.id} className="border border-fg/15 overflow-hidden bg-fg/[0.02]" data-testid={`rencontre-item-${r.id}`}>
                <div className="aspect-[3/4] bg-fg/5 relative overflow-hidden">
                  <img src={r.image_url} alt={r.location || ""} className="w-full h-full object-cover" />
                  {r.featured && (
                    <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-cinabre text-bg font-mono text-[9px] tracking-[0.2em] uppercase">
                      Coup de cœur
                    </div>
                  )}
                  <div className={`absolute top-2 right-2 px-1.5 py-0.5 font-mono text-[9px] tracking-[0.2em] uppercase ${r.status === "approved" ? "bg-emerald-500/20 text-emerald-800" : r.status === "pending" ? "bg-amber-500/20 text-amber-800" : "bg-fg/60 text-bg"}`}>
                    {r.status === "approved" ? "Visible" : r.status === "pending" ? "En attente" : "Rejetée"}
                  </div>
                </div>
                <div className="p-3 space-y-1.5">
                  {(r.location || r.taken_at) && (
                    <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-fg/60">
                      {[r.taken_at, r.location].filter(Boolean).join(" · ")}
                    </div>
                  )}
                  {r.photographer_name && (
                    <div className="font-serif text-sm">— {r.photographer_name}</div>
                  )}
                  <div className="flex items-center gap-3 pt-2 border-t border-fg/10">
                    <button onClick={() => editItem(r)} className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/70 hover:text-cinabre" data-testid={`rencontre-edit-${r.id}`}>
                      Modifier
                    </button>
                    <button onClick={() => remove(r.id)} className="ml-auto font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50 hover:text-red-600" data-testid={`rencontre-delete-${r.id}`}>
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Commentaires · Modération ───────────────────────────────────────────────
function CommentsPanel({ items, onSaved }) {
  const [filter, setFilter] = useState("all"); // all | approved | hidden

  const shown = items.filter((c) => filter === "all" ? true : c.status === filter);
  const approved = items.filter((c) => c.status === "approved").length;
  const hidden = items.filter((c) => c.status === "hidden").length;

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/comments/${id}/status`, { status });
      toast.success(status === "approved" ? "Commentaire visible." : "Commentaire masqué.");
      onSaved?.();
    } catch { toast.error("Action impossible."); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement ce commentaire ?")) return;
    try {
      await api.delete(`/admin/comments/${id}`);
      toast.success("Supprimé.");
      onSaved?.();
    } catch { toast.error("Suppression impossible."); }
  };

  return (
    <div className="space-y-8" data-testid="admin-comments">
      <div>
        <div className="meta-label text-cinabre">§ Modération · Voix du Carnet</div>
        <h2 className="font-serif text-3xl md:text-4xl mt-2 tracking-tight">Commentaires</h2>
        <p className="mt-2 text-sm text-fg/60 max-w-2xl">
          Les commentaires publiés par les lecteurs sur les chroniques du Carnet. Masquez ou supprimez ce qui ne trouve pas sa place.
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {[
          { k: "all", l: `Tous (${items.length})` },
          { k: "approved", l: `Visibles (${approved})` },
          { k: "hidden", l: `Masqués (${hidden})` },
        ].map(f => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            className={`px-3.5 py-1.5 text-[13px] font-serif border transition-all ${filter === f.k ? "bg-cinabre text-bg border-cinabre" : "bg-transparent text-fg/80 border-fg/25 hover:border-cinabre hover:text-cinabre"}`}
            data-testid={`comment-filter-${f.k}`}
          >
            {f.l}
          </button>
        ))}
      </div>

      {shown.length === 0 && (
        <div className="border border-dashed border-fg/20 p-6 text-center text-sm text-fg/50">
          Aucun commentaire.
        </div>
      )}

      <div className="space-y-4">
        {shown.map((c) => (
          <div key={c.id} className="border border-fg/15 p-5 md:p-6" data-testid={`comment-row-${c.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="font-serif text-lg leading-tight">{c.name}</div>
                <div className="mt-1 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/55">
                  {c.email} · /carnet/{c.slug} · {c.created_at?.slice(0, 10)}
                </div>
              </div>
              <span className={`inline-block px-2 py-0.5 font-mono text-[9px] tracking-[0.2em] uppercase whitespace-nowrap ${c.status === "approved" ? "bg-emerald-500/15 text-emerald-700" : "bg-fg/60 text-bg"}`}>
                {c.status === "approved" ? "Visible" : "Masqué"}
              </span>
            </div>
            <p className="mt-3 font-serif text-base leading-relaxed text-fg/85 whitespace-pre-wrap">
              {c.content}
            </p>
            <div className="mt-4 flex items-center gap-3 border-t border-fg/10 pt-3">
              {c.status === "approved" ? (
                <button onClick={() => setStatus(c.id, "hidden")} className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/70 hover:text-cinabre" data-testid={`comment-hide-${c.id}`}>
                  Masquer
                </button>
              ) : (
                <button onClick={() => setStatus(c.id, "approved")} className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre hover:underline" data-testid={`comment-approve-${c.id}`}>
                  Rendre visible
                </button>
              )}
              <a href={`/carnet/${c.slug}`} target="_blank" rel="noreferrer" className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/70 hover:text-cinabre">
                Voir l'article ↗
              </a>
              <button onClick={() => remove(c.id)} className="ml-auto font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50 hover:text-red-600" data-testid={`comment-delete-${c.id}`}>
                <Trash2 size={11} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

